import { User } from "../types";
import { API_ENDPOINTS, LS_ACCESS, LS_REFRESH, LS_USER } from "../config/api";

interface FetchOptions extends RequestInit {
  // Permette di tipizzare alcune opzioni extra che usiamo nel nostro wrapper di fetch.
  method?: string;
  body?: string;
  // Se true, la richiesta parte senza token di autenticazione.
  skipAuth?: boolean;
}

export const AUTH_SESSION_EVENT = "auth-session-changed";

export class ApiError extends Error {
  // Codice HTTP restituito dal backend, ad esempio 400 o 401.
  status: number;
  // Corpo completo della risposta di errore, utile per fare debug.
  data: unknown;
  // Messaggio di errore già pronto da mostrare o loggare.
  detail: string;

  constructor(status: number, detail: string, data: unknown = null) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.detail = detail;
  }
}

type AuthSessionPayload = {
  // L'utente attualmente autenticato. Se è null significa sessione assente o rimossa.
  user: User | null;
};

const emitAuthSessionEvent = (payload: AuthSessionPayload) => {
  // Invia un evento globale nel browser.
  // Serve per avvisare altri componenti che la sessione è cambiata
  // senza doverli collegare direttamente tra loro.
  window.dispatchEvent(new CustomEvent<AuthSessionPayload>(AUTH_SESSION_EVENT, { detail: payload }));
};

export const getStoredUser = (): User | null => {
  // Recupera l'utente salvato nel localStorage.
  // Il localStorage può contenere solo stringhe, quindi qui troveremo JSON testuale.
  const storedUser = localStorage.getItem(LS_USER);
  if (!storedUser) {
    return null;
  }
  try {
    // Convertiamo la stringa JSON in un oggetto User vero e proprio.
    return JSON.parse(storedUser) as User;
  } catch (error) {
    // Se il JSON è corrotto o non valido, puliamo tutta la sessione
    // per evitare che l'app lavori con dati incoerenti.
    console.error("Errore nel parsing dell'utente:", error);
    clearAuthSession();
    return null;
  }
};

export const saveAuthSession = (access: string, refresh: string, user: User) => {
  // Salva tutto il necessario per mantenere la sessione tra un refresh pagina e l'altro.
  localStorage.setItem(LS_ACCESS, access);
  localStorage.setItem(LS_REFRESH, refresh);
  localStorage.setItem(LS_USER, JSON.stringify(user));
  // Notifica il resto dell'app che adesso esiste una sessione valida.
  emitAuthSessionEvent({ user });
};

export const updateStoredUser = (user: User | null) => {
  // Aggiorna solo i dati utente, senza toccare i token.
  // Utile quando cambiano nome, profilo o altre info dell'utente loggato.
  if (user) {
    localStorage.setItem(LS_USER, JSON.stringify(user));
  } else {
    // Se arriva null, rimuoviamo l'utente salvato.
    localStorage.removeItem(LS_USER);
  }
  emitAuthSessionEvent({ user });
};

export const clearAuthSession = () => {
  // Cancella completamente la sessione locale.
  // Lo facciamo ad esempio quando il refresh token non è più valido.
  localStorage.removeItem(LS_ACCESS);
  localStorage.removeItem(LS_REFRESH);
  localStorage.removeItem(LS_USER);
  emitAuthSessionEvent({ user: null });
};

const buildHeaders = (
  baseHeaders: HeadersInit | undefined,
  token: string | null,
  skipAuth: boolean | undefined
): HeadersInit => ({
  // Imposta gli header di base per tutte le richieste JSON.
  "Content-Type": "application/json",
  // Mantiene eventuali header passati dal chiamante.
  ...(baseHeaders || {}),
  // Aggiunge automaticamente il token Bearer se serve autenticazione.
  ...(!skipAuth && token ? { Authorization: `Bearer ${token}` } : {}),
});

const parseApiError = async (response: Response): Promise<ApiError> => {
  // Prova a leggere il corpo della risposta per estrarre un messaggio più utile.
  const data = await response.json().catch(() => null);
  const detail =
    typeof data === "string"
      ? data
      : (data as Record<string, unknown> | null)?.detail?.toString?.() ||
        (data as Record<string, unknown> | null)?.message?.toString?.() ||
        // Fallback usato se il backend non ci dà un messaggio leggibile.
        `API error! Code: ${response.status}`;
  return new ApiError(response.status, detail, data);
};

const refreshAccessToken = async (): Promise<string | null> => {
  // Legge il refresh token salvato localmente.
  // Se non esiste, non possiamo ottenere un nuovo access token.
  const refresh = localStorage.getItem(LS_REFRESH);
  if (!refresh) {
    return null;
  }

  // Chiede al backend un nuovo access token usando il refresh token.
  const refreshRes = await fetch(API_ENDPOINTS.TOKEN_REFRESH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });

  if (!refreshRes.ok) {
    // Se il refresh fallisce, consideriamo la sessione scaduta o non valida.
    clearAuthSession();
    return null;
  }

  const data = await refreshRes.json();
  // Salviamo il nuovo access token per le prossime richieste.
  localStorage.setItem(LS_ACCESS, data.access);
  return data.access as string;
};

export const apiFetch = async <T = any,>(
  endpoint: string,
  options: FetchOptions = { method: "GET" }
): Promise<T> => {
  // Estraiamo skipAuth e headers per gestirli in modo centralizzato,
  // mentre tutto il resto viene passato direttamente a fetch.
  const { skipAuth, headers, ...rest } = options;
  const token = localStorage.getItem(LS_ACCESS);

  const init: RequestInit = {
    ...rest,
    headers: buildHeaders(headers, token, skipAuth),
  };

  let res = await fetch(endpoint, init);

  if (res.status === 401 && !skipAuth) {
    // Se il token è scaduto, proviamo una sola volta a rigenerarlo
    // e poi ripetiamo la stessa richiesta.
    const refreshedAccess = await refreshAccessToken();
    if (refreshedAccess) {
      init.headers = buildHeaders(headers, refreshedAccess, false);
      res = await fetch(endpoint, init);
    }
  }

  if (!res.ok) {
    // Trasformiamo l'errore HTTP in un oggetto uniforme per tutta l'app.
    throw await parseApiError(res);
  }

  if (res.status === 204) {
    // 204 significa "nessun contenuto": la richiesta è andata bene,
    // ma il backend non restituisce JSON.
    return undefined as T;
  }

  return (await res.json()) as T;
};

// Versione "estesa" del nome: nome + cognome se il cognome è presente,
// altrimenti solo il nome (fallback allo username se mancano entrambi).
// È la fonte di verità per ogni visualizzazione in formato stringa.
export function userFullName(u: User): string {
  const fn = u.first_name?.trim();
  const ln = u.last_name?.trim();
  return (fn || ln) ? [fn, ln].filter(Boolean).join(" ") : u.username;
}

// Versione "da elenco": cognome + nome (es. "Rossi Mario"). Va usata ovunque la
// lista sia ordinata per cognome (select, filtri, dropdown), altrimenti l'ordine
// risulta incomprensibile perché il testo mostrato parte dal nome.
export function userSortName(u: User): string {
  const fn = u.first_name?.trim();
  const ln = u.last_name?.trim();
  return (fn || ln) ? [ln, fn].filter(Boolean).join(" ") : u.username;
}

// Comparatore unico per ordinare gli utenti per cognome + nome: tiene allineati
// ordinamento e testo mostrato da `userSortName`.
export function compareUsersByName(a: User, b: User): number {
  return userSortName(a).localeCompare(userSortName(b), "it", { sensitivity: "base" });
}

// Nome + iniziale del cognome puntata (es. "Mario R."); senza cognome solo il
// nome; senza nome ripieghiamo sullo username.
export function userNameWithInitial(u: User): string {
  const fn = u.first_name?.trim();
  const ln = u.last_name?.trim();
  if (!fn) return u.username;
  return ln ? `${fn} ${ln[0]}.` : fn;
}

// Versione "corta" pensata per spazi ridotti (mobile): cognome + iniziale del
// nome puntata (es. "Rossi M."); senza cognome usiamo il nome intero.
export function userShortName(u: User): string {
  const fn = u.first_name?.trim();
  const ln = u.last_name?.trim();
  const initial = fn ? `${fn[0]}.` : "";
  return ln ? [ln, initial].filter(Boolean).join(" ") : (fn || u.username);
}

export function userLabel(u: User) {
  return (
    <>
      {/* Su schermi medi/grandi mostriamo il nome completo. */}
      <span className="hidden md:inline-block">{userFullName(u)}</span>
      {/* Su mobile usiamo una versione più corta per risparmiare spazio. */}
      <span className="md:hidden">{userShortName(u)}</span>
    </>
  )
}

export function controllaInsulti(text: string) {
  // Lista semplice di parole da intercettare.
  // Il controllo finale richiede anche la presenza di "fsm" per evitare falsi positivi troppo generici.
  const insulti = ["porc", "cagna", "schifo", "merda", "puttana", "stronza", "infame", "maledetta", "fottuta", "vacca", "lurida"];
  return insulti.some(insulto => text.toLowerCase().includes(insulto)) && text.toLowerCase().includes("fsm");
};
